import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { LocalizationService } from './localization-service';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private baseUrl = environment.apiBaseUrl;
  private http = inject(HttpClient);
  private router = inject(Router);
  private readonly localizationService = inject(LocalizationService);
  // Capture host origin once; fallback to empty string if unavailable (SSR-safe guard)
  private readonly webDomain: string = (typeof window !== 'undefined' && window.location ? window.location.origin : '');

  private handleHttpError = (error: HttpErrorResponse): Observable<never> => {
    return throwError(() => error);
  };

  private resolveLanguage(expectedLang?: string): string {
    return expectedLang || this.localizationService.getCurrentLang() || environment.defaultLang;
  }

  private buildHeaders(lang?: string, webDomainOverride?: string): HttpHeaders {
    const selectedLang = lang || environment.defaultLang;
    const host = webDomainOverride || this.webDomain;
    return new HttpHeaders()
      .set('Accept-Language', selectedLang)
      .set('WebDomain', host);
  }

  getData(endpoint: string, expectedLang?: string): Observable<any> {
    const lang = this.resolveLanguage(expectedLang);
    const headers = this.buildHeaders(lang);

    return this.http.get(`${this.baseUrl}/${endpoint}`, { headers })
      .pipe(catchError(this.handleHttpError));
  }
  postData(endpoint: string, payload: any, expectedLang?: string): Observable<any> {
    const lang = this.resolveLanguage(expectedLang);
    const headers = this.buildHeaders(lang);

    return this.http.post(`${this.baseUrl}/${endpoint}`, payload, { headers })
      .pipe(catchError(this.handleHttpError));
  }

  putData(endpoint: string, payload: any, expectedLang?: string): Observable<any> {
    const lang = this.resolveLanguage(expectedLang);
    const headers = this.buildHeaders(lang);

    return this.http.post(`${this.baseUrl}/${endpoint}`, payload, { headers })
      .pipe(catchError(this.handleHttpError));
  }

  delete(endpoint: string, expectedLang?: string): Observable<any> {
    const lang = this.resolveLanguage(expectedLang);
    const headers = this.buildHeaders(lang);

    return this.http.post(`${this.baseUrl}/${endpoint}`, { headers })
      .pipe(catchError(this.handleHttpError));
  }

  initializeTenantUserWithRoles(payload: any, webDomain: string): Observable<any> {
    const lang = this.resolveLanguage();
    const headers = this.buildHeaders(lang, webDomain);

    return this.http.post(`${this.baseUrl}/api/v1/Tenant/InitializeTenantUserWithRoles`, payload, { headers })
      .pipe(catchError(this.handleHttpError));
  }

  confirmMailPost(payload: any, webDomain: string): Observable<any> {
    const lang = this.resolveLanguage();
    const headers = this.buildHeaders(lang, webDomain);

    return this.http.post(`${this.baseUrl}/api/v1/Account/ConfirmMailPost`, payload, { headers })
      .pipe(catchError(this.handleHttpError));
  }

}
